import React, { lazy, Suspense, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import cx from 'classnames'
import { Button, Classification, Quote } from '~elements'
import styles from './EDPFullScreenVideo.module.scss'
import { AssetProps, ButtonProps, ClassificationProps, ImageProps, ResponsiveMultimedia, RichTextType } from '~types'
import { getCustomBreakpoint } from 'utils/hooks'
import breakpoints from '../../../styles/breakpoints.json'
import { getImageUrl } from 'utils/assets/getImageUrl'

const Video = lazy(() => import('components/elements/Video/Video'))

// Interfaces (consider moving to shared location)
interface QuoteProps {
  quoteTitle: string
  quoteDescription: RichTextType
  quoteSubHeadline: string
  quoteCopy: RichTextType
}

interface HygraphQuoteProps extends Partial<QuoteProps>, Partial<ClassificationProps> {}

interface EDPFullScreenVideoProps {
  alignCtaBottom?: boolean
  backgroundVideo?: ResponsiveMultimedia & { videoLoop: boolean }
  ctaPrimary?: ButtonProps
  quote?: HygraphQuoteProps[]
}

const createResponsiveAssetProps = (
  imageSize: 'xl' | 'md' | 'sm',
  posterImage: AssetProps | undefined,
  fallbackS3Url?: string | undefined
): AssetProps => {
  const defaults = {
    xl: {
      fileName: 'imageXl',
      width: 1920,
      height: 1080,
      altText: 'image-xl'
    },
    md: {
      fileName: 'imageMd',
      width: 1024,
      height: 768,
      altText: 'image-md'
    },
    sm: {
      fileName: 'imageSm',
      width: 640,
      height: 480,
      altText: 'image-sm'
    }
  }[imageSize]

  return {
    fileName: posterImage?.fileName ?? defaults.fileName,
    width: posterImage?.width ?? defaults.width,
    height: posterImage?.height ?? defaults.height,
    altText: posterImage?.altText ?? defaults.altText,
    url:
      getImageUrl({
        imageUrl: posterImage?.url,
        fallbackS3URL: fallbackS3Url
      }) ?? ''
  }
}

const EDPFullScreenVideo = ({ alignCtaBottom = true, backgroundVideo, ctaPrimary, quote }: EDPFullScreenVideoProps) => {
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => setIsMounted(true), [])

  const { isBreakpoint: isAboveMd } = getCustomBreakpoint(breakpoints.md, '>')

  const {
    backgroundVideoXl,
    backgroundVideoMd,
    backgroundVideoSm,
    videoLoop,
    posterImageXl,
    posterImageMd,
    posterImageSm
  } = backgroundVideo ?? {}

  const imageXl = createResponsiveAssetProps('xl', posterImageXl)
  const imageMd = createResponsiveAssetProps('md', posterImageMd)
  const imageSm = createResponsiveAssetProps('sm', posterImageSm)

  const [wrappedQuote] = quote ?? []
  const quoteObject = wrappedQuote?.quoteTitle ? (wrappedQuote as QuoteProps) : null
  const classification = wrappedQuote?.title ? (wrappedQuote as ClassificationProps) : null

  const CtaButton = () => {
    if (!ctaPrimary || !isMounted) return null
    const portalRoot = typeof document !== 'undefined' && document.getElementById('portal-root')

    const button = (
      <div className={styles['cta-container']}>
        <Button
          {...ctaPrimary}
          isFullWidth={!isAboveMd && alignCtaBottom}
          analytics={{
            linkModule: 'edp_full_screen_video'
          }}
        />
      </div>
    )

    // Button needs to sit fixed outside of document flow on mobile so we use createPortal
    // Added global .portal-fixed-cta-container class as css module does not work
    return !isAboveMd && portalRoot && alignCtaBottom
      ? ReactDOM.createPortal(<div className='portal-fixed-cta-container'>{button}</div>, portalRoot)
      : button
  }

  return (
    <div
      className={cx(styles['edp-full-screen-video'], {
        [styles['align-cta-bottom']]: alignCtaBottom
      })}
    >
      <div className={styles['copy-cta-container']}>
        <div className={styles['copy-container']}>
          {quoteObject && <Quote classification={quoteObject} />}
          {classification && (
            <Classification
              title={classification?.title}
              description={classification?.description}
              ratingNumber={classification?.ratingNumber}
              ratingImage={classification?.ratingImage}
            />
          )}
          <CtaButton />
        </div>
      </div>

      <div className={styles['video-container']}>
        <Suspense fallback={<>Loading...</>}>
          <Video
            videoLoop={Boolean(videoLoop)}
            backgroundVideoXl={backgroundVideoXl}
            backgroundVideoMd={backgroundVideoMd}
            backgroundVideoSm={backgroundVideoSm}
            posterImageXl={imageXl}
            posterImageMd={imageMd}
            posterImageSm={imageSm}
          />
        </Suspense>
      </div>
    </div>
  )
}

export default EDPFullScreenVideo
